/**
 * DomainErrorBanner.tsx
 *
 * Per-domain error banner. Extended in Slice E to be category-aware via the
 * `errorEnvelope` prop — switches color family, icon, and copy based on
 * `SyncErrorCode`.
 *
 * Four category variants rendered (AUTH_EXPIRED and RATE_LIMIT route to their
 * dedicated canvas-level banners; BUSINESS_EMPTY has no banner):
 *   SCHEMA_DRIFT   → danger/red  + AlertCircle
 *   TRANSIENT_5XX  → warning/amber + AlertTriangle
 *   NETWORK        → warning/amber + WifiOff   (brand guide §34.12 — new in this epic)
 *   PLATFORM       → warning/amber + AlertTriangle
 *
 * Multi-domain stacking: DomainErrorBannerStack renders one DomainErrorBannerSingle
 * per failed domain, wrapped in role="group" when multiple banners are present.
 *
 * Brand guide §34.6. Mockup: 2026-05-05-domain-error-banner-v1.html.
 *
 * Accessibility (brand guide §34.10):
 *   - role="alert" aria-live="assertive" on each banner
 *   - role="group" aria-label="Domain loading errors" on multi-domain stack
 *   - Retry button: aria-label="Retry loading {domainName} data"
 *   - Retry in-flight: Loader2 spinner + "Retrying…", button disabled
 */

import React from 'react'
import { AlertTriangle, AlertCircle, WifiOff, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { SyncErrorEnvelope } from '@/types/SyncError.types'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DomainErrorEntry {
  /** Unique domain identifier (e.g. "financial", "enrollment", "bunks"). */
  domainId: string
  /**
   * Human-readable display name (e.g. "Financial", "Enrollment", "Bunks").
   * Rendered in the banner copy: "{displayName} data: …"
   */
  displayName: string
  /**
   * Structured error envelope for category-aware copy + styling (Slice E).
   * When absent, falls back to the generic amber warning copy.
   */
  errorEnvelope?: SyncErrorEnvelope | null
}

// ─── Category config ──────────────────────────────────────────────────────────

interface CategoryConfig {
  icon: React.ReactNode
  wrapperClass: string
  bodyClass: string
  buttonVariant: 'destructive' | 'outline'
  buttonClass: string
  makeBodyCopy: () => string
}

/**
 * Returns style + copy config for the given error code.
 *
 * AUTH_EXPIRED → canvas-level AuthExpiredBanner.
 * RATE_LIMIT   → canvas-level RateLimitBanner.
 * BUSINESS_EMPTY → no banner (panel chip only).
 *
 * All other codes fall through the switch — TRANSIENT_5XX is the default
 * because it is the most common unexpected category.
 */
function getCategoryConfig(code: string | undefined): CategoryConfig {
  switch (code) {
    case 'SCHEMA_DRIFT':
      return {
        icon: (
          <AlertCircle
            size={15}
            className="text-[hsl(0_63%_42%)] flex-shrink-0"
            aria-hidden="true"
          />
        ),
        wrapperClass: 'bg-destructive/5 border-b border-destructive/20',
        bodyClass: 'text-[hsl(0_63%_42%)]',
        buttonVariant: 'destructive',
        buttonClass: '',
        makeBodyCopy: () =>
          `We hit an unexpected response from CampMinder. Your data may be partial. We've been notified.`,
      }

    case 'NETWORK':
      return {
        icon: (
          <WifiOff
            size={15}
            className="text-[hsl(34_100%_30%)] flex-shrink-0"
            aria-hidden="true"
          />
        ),
        wrapperClass: 'bg-warning-foreground border-b border-warning/30',
        bodyClass: 'text-[hsl(34_100%_30%)]',
        buttonVariant: 'outline',
        buttonClass:
          'border-[hsl(34_100%_30%)] text-[hsl(34_100%_30%)] hover:bg-warning-foreground/70',
        makeBodyCopy: () =>
          `We couldn't reach CampMinder. Check your connection and try again.`,
      }

    case 'PLATFORM':
      return {
        icon: (
          <AlertTriangle
            size={15}
            className="text-[hsl(34_100%_30%)] flex-shrink-0"
            aria-hidden="true"
          />
        ),
        wrapperClass: 'bg-warning-foreground border-b border-warning/30',
        bodyClass: 'text-[hsl(34_100%_30%)]',
        buttonVariant: 'outline',
        buttonClass:
          'border-[hsl(34_100%_30%)] text-[hsl(34_100%_30%)] hover:bg-warning-foreground/70',
        makeBodyCopy: () =>
          `Something went wrong on our end. We've been notified. Try again in a moment.`,
      }

    // TRANSIENT_5XX and any unrecognised code
    default:
      return {
        icon: (
          <AlertTriangle
            size={15}
            className="text-[hsl(34_100%_30%)] flex-shrink-0"
            aria-hidden="true"
          />
        ),
        wrapperClass: 'bg-warning-foreground border-b border-warning/30',
        bodyClass: 'text-[hsl(34_100%_30%)]',
        buttonVariant: 'outline',
        buttonClass:
          'border-[hsl(34_100%_30%)] text-[hsl(34_100%_30%)] hover:bg-warning-foreground/70',
        makeBodyCopy: () =>
          `CampMinder seems to be having trouble right now. Try again in a few minutes.`,
      }
  }
}

// ─── Single banner ────────────────────────────────────────────────────────────

interface DomainErrorBannerSingleProps extends DomainErrorEntry {
  /** Whether a retry is currently in-flight for this domain. */
  isRetrying: boolean
  /** Called when the user clicks Retry. */
  onRetry: (domainId: string) => void
}

/**
 * A single domain error banner — category-aware when errorEnvelope is provided.
 * Exported for testing and for the multi-domain wrapper.
 */
export const DomainErrorBannerSingle: React.FC<DomainErrorBannerSingleProps> = ({
  domainId,
  displayName,
  errorEnvelope,
  isRetrying,
  onRetry,
}) => {
  const config = getCategoryConfig(errorEnvelope?.code)

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 px-6 py-2.5 flex-shrink-0',
        config.wrapperClass,
      )}
      role="alert"
      aria-live="assertive"
      data-testid={`domain-error-banner-${domainId}`}
    >
      {config.icon}
      <span className={cn('flex-1 text-sm font-medium min-w-0', config.bodyClass)}>
        <strong className="font-semibold">{displayName}:</strong>{' '}
        {config.makeBodyCopy()}
      </span>
      <Button
        size="sm"
        variant={config.buttonVariant}
        disabled={isRetrying}
        onClick={() => onRetry(domainId)}
        aria-label={`Retry loading ${displayName} data`}
        className={cn('flex items-center gap-1.5 font-semibold flex-shrink-0', config.buttonClass)}
        data-testid={`btn-retry-${domainId}`}
      >
        {isRetrying ? (
          <>
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
            Retrying…
          </>
        ) : (
          'Retry'
        )}
      </Button>
    </div>
  )
}

// ─── Multi-domain stack ───────────────────────────────────────────────────────

interface DomainErrorBannerStackProps {
  /** All domains currently in error state. */
  failedDomains: DomainErrorEntry[]
  /**
   * Set of domainIds currently being retried (in-flight).
   * Disables the Retry button while a request is pending.
   */
  retryingDomainIds: ReadonlySet<string>
  /** Called when the user clicks Retry on a banner. */
  onRetry: (domainId: string) => void
}

/**
 * DomainErrorBannerStack — renders one DomainErrorBannerSingle per failed domain.
 * Wraps multiple banners in role="group" per brand guide §34.6.
 *
 * Single-domain variant renders without the wrapper group element (the wrapper
 * only aids AT users when there are 2+ banners).
 *
 * If failedDomains is empty, renders nothing.
 */
export const DomainErrorBannerStack: React.FC<DomainErrorBannerStackProps> = ({
  failedDomains,
  retryingDomainIds,
  onRetry,
}) => {
  if (failedDomains.length === 0) return null

  if (failedDomains.length === 1) {
    const domain = failedDomains[0]
    return (
      <DomainErrorBannerSingle
        domainId={domain.domainId}
        displayName={domain.displayName}
        errorEnvelope={domain.errorEnvelope}
        isRetrying={retryingDomainIds.has(domain.domainId)}
        onRetry={onRetry}
      />
    )
  }

  return (
    <div
      role="group"
      aria-label="Domain loading errors"
      className="flex-shrink-0"
      data-testid="domain-error-banner-stack"
    >
      {failedDomains.map(domain => (
        <DomainErrorBannerSingle
          key={domain.domainId}
          domainId={domain.domainId}
          displayName={domain.displayName}
          errorEnvelope={domain.errorEnvelope}
          isRetrying={retryingDomainIds.has(domain.domainId)}
          onRetry={onRetry}
        />
      ))}
    </div>
  )
}
